import { useEffect, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import Autosave from "../../Autosave";
import Button from "../../Button";
import { Ontario } from "../../Icons/misc";
import { Text } from "../../Type";
import styles from "./index.module.scss";
import { ArrowRight } from "../../Icons/general-interface-1";
import { useStoreContext } from "../../../contexts/StoreProvider";
import { isAuthorizer as checkIsAuthorizer, isAdmin as checkIsAdmin, isSalesRep as checkIsSalesRep } from "../../../utils/user";
import { useSectionsValid } from "../../../hooks/useSectionsValid";
import { FileStatusType } from "../../../types/fileStatusType";
import Modal from "../../Modal";
import SignerSelectionForm from "../SignerSelectionForm";

const GET_CLIENT_FILE = gql`
  query GetClientFile($id: ID!) {
    clientFile(id: $id) {
      id
      status
      signing_authorizer {
        id
        first_name
        last_name
      }
      signing_sales_rep {
        id
        first_name
        last_name
      }
      client {
        id
        first_name
        last_name
        social_assistance
        health_card
        health_card_version
        birth_date
        long_term_care_home
        sex
        cep
        address {
          address_1
          address_2
          unit_number
          street_number
          street_name
          lot
          business_phone
          business_phone_ext
          postal_code
          city
          province
          phone
          fax
        }
        agent {
          unit_number
          street_number
          street_name
          lot
          business_phone
          business_phone_ext
          postal_code
          city
          province
          phone
          fax
        }
        authorizers {
          id
          first_name
          last_name
          role {
            type
            name
          }
        }
        sales_reps {
          id
          first_name
          last_name
          role {
            type
            name
          }
          locations {
            id
            name
            adp_auth_num
          }
          cep
        }
      }
      client_form {
        id
        form_data
        updated_at
      }
      ds_envelope_id
      # file_events
      client_signature {
        name
        url
      }
      justification {
        name
        url
      }
      letter_of_rationale {
        name
        url
      }
      quote {
        name
        url
      }
      confirmation_of_benefits {
        name
        url
      }
      other_supporting_documents {
        name
        url
      }
      other_supporting_documents_alt {
        name
        url
      }
      wet_signature {
        name
        url
      }
      ds_signed_form {
        name
        url
      }
      ds_signed_form_original {
        name
        url
      }
      form_xml {
        name
        url
      }
      locked_by {
        id
        first_name
        last_name
      }
      locked_at
    }
  }
`;

const SET_IN_PROGRESS = gql`
  mutation SetInProgress($input: SendForXInput) {
    setInProgress(input: $input) {
      message
    }
  }
`;

const SEND_FOR_REVIEW = gql`
  mutation SendForReview($input: SendForXInput) {
    sendForReview(input: $input) {
      message
    }
  }
`;

const SEND_FOR_CHANGES = gql`
  mutation SendForChanges($input: SendForXInput) {
    sendForChanges(input: $input) {
      message
    }
  }
`;

const SEND_FOR_SIGNATURES = gql`
  mutation SendForSignatures($input: SendForSignaturesInput) {
    sendForSignatures(input: $input) {
      message
    }
  }
`;

type PropsType = {
  formId: string;
  fileStatus: FileStatusType;
  noButtons?: boolean;
  hasSalesRep?: boolean;
  summaryMode?: boolean;
  setViewStatus;
  formMode: string;
};

const SaveHeader = ({
  formId,
  fileStatus,
  noButtons,
  hasSalesRep,
  summaryMode = false,
  setViewStatus,
  formMode,
}: PropsType) => {
  const { state: { user } } = useStoreContext();
  const [submitting, setSubmitting] = useState(false);
  const [signerSelectionModalOpen, setSignerSelectionModalOpen] = useState(false);
  const [, setBoundaryError] = useState<Error | null>(null);
  const isAuthorizer = checkIsAuthorizer(user) || checkIsAdmin(user);
  const isSalesRep = checkIsSalesRep(user);

  const urlParams = new URLSearchParams(window.location.search);
  const event = urlParams.get("event");
  const signingInitialized = (event === "signing_complete" && fileStatus === "PendingSignature");

  // Red Errors Check for all sections (enable/disable submit button) & if Sales Rep is attached
  const enabledReview = useSectionsValid(true).all && hasSalesRep && ["Progress", "Action"].includes(fileStatus) && formMode !== "locked";
  const enableSigning = useSectionsValid(true).all && hasSalesRep && !["locked", "waitingSigning"].includes(formMode) && fileStatus !== "PendingSignature";
  const enableComments = useSectionsValid(true).all && fileStatus === "Review";

  const [setInProgress] = useMutation(SET_IN_PROGRESS, {
    refetchQueries: [GET_CLIENT_FILE],
    awaitRefetchQueries: true,
  });

  // If Authorizer opens form when form was previously notStarted, set to In Progress
  useEffect(() => {
    if (fileStatus === "NotStarted" && isAuthorizer) {
      setInProgress({
        variables: {
          input: {
            formId,
          },
        },
      }).catch((error) => {
        setBoundaryError(() => {
          throw error;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStatus, formId]);

  const [sendingForReview] = useMutation(SEND_FOR_REVIEW, {
    onCompleted: () => {
      setSubmitting(false);
      setViewStatus(true);
      window.scrollTo(0, 0);
    },
    refetchQueries: [GET_CLIENT_FILE],
    awaitRefetchQueries: true,
  });

  const [sendingForChanges] = useMutation(SEND_FOR_CHANGES, {
    onCompleted: () => {
      setViewStatus(true);
      setSubmitting(false);
      window.scrollTo(0, 0);
    },
    refetchQueries: [GET_CLIENT_FILE],
    awaitRefetchQueries: true,
  });

  const [sendingForSignatures] = useMutation(SEND_FOR_SIGNATURES, {
    onCompleted: () => {
      setViewStatus(true);
      setSubmitting(false);
      window.scrollTo(0, 0);
    },
    refetchQueries: [GET_CLIENT_FILE],
    awaitRefetchQueries: true,
  });

  const handleSignatureSelectionSubmit = ({
    authorizerId,
    salesRepId,
    locationId,
    cepNew,
    cepRecycled,
  }: {
    authorizerId?: string;
    salesRepId: string;
    locationId: string;
    cepNew: boolean;
    cepRecycled: boolean;
  }) => {
    setSubmitting(true);
    setSignerSelectionModalOpen(false);
    sendingForSignatures({
      variables: {
        input: {
          formId,
          authorizerId,
          salesRepId,
          locationId,
          cepNew,
          cepRecycled,
          skipReview: (fileStatus === "Progress")
        },
      },
    }).catch((error) => {
      setBoundaryError(() => {
        throw error;
      });
    });
  };

  const buttonState = (() => {
    if (!summaryMode) {
      return (
        <Button
          submit
          className={styles.submitButton}
          disabled={signingInitialized}
          primary
          icon={<ArrowRight />}
          onClick={() => {
            setViewStatus(formMode === "waitingSigning");
            window.scrollTo(0, 0);
          }}
        >
          Overview
        </Button>
      );
    }
    if (summaryMode && !isSalesRep) {
      return (
        <>
          <Button
            className={styles.submitButton}
            primary
            icon={<ArrowRight />}
            disabled={!enabledReview || submitting}
            onClick={() => {
              setSubmitting(true);
              sendingForReview({
                variables: {
                  input: {
                    formId,
                  },
                },
              }).catch((error) => {
                setBoundaryError(() => {
                  throw error;
                });
              });
            }}
          >
            Submit for Review
          </Button>
          &nbsp; &nbsp;
          <Button
            className={styles.submitButton}
            primary
            icon={<ArrowRight />}
            disabled={!enableSigning || submitting}
            onClick={() => {setSignerSelectionModalOpen(true);}}
          >
            Submit for Signatures
          </Button>
        </>
      );
    }
    if (summaryMode && isSalesRep) {
      return (
        <>
          <Button
            className={styles.submitButton}
            primary
            icon={<ArrowRight />}
            disabled={!enableComments || submitting}
            onClick={() => {
              setSubmitting(true);
              sendingForChanges({
                variables: {
                  input: {
                    formId,
                  },
                },
              }).catch((error) => {
                setBoundaryError(() => {
                  throw error;
                });
              });
            }}
          >
            Submit Comments
          </Button>
          &nbsp; &nbsp;
          <Button
            className={styles.submitButton}
            primary
            icon={<ArrowRight />}
            disabled={!enableSigning || submitting}
            onClick={() => {setSignerSelectionModalOpen(true);}}
          >
            Submit for Signatures
          </Button>
        </>
      );
    }

    return null;
  })();

  return (
    <div className={styles.header}>
      <Ontario />

      <Text className={styles.text} component="span" weight="bold" size="large">
        Application for Funding Mobility Devices
      </Text>
      <span className={styles.rightGroup}>
        <Autosave className={styles.saveInfo} disabled={summaryMode}/>
        {!noButtons && buttonState}
      </span>

      <Modal
        title="Vendor Information"
        isOpen={signerSelectionModalOpen}
        onClose={() => setSignerSelectionModalOpen(false)}
      >
        <SignerSelectionForm
          onSubmit={handleSignatureSelectionSubmit}
          skip
        />
      </Modal>
    </div>
  );
};

export default SaveHeader;
